'use client'

// useModel — a drop-in replacement for drei's useGLTF that wires BOTH
// vendored loaders: the Draco decoder (public/draco) and the KTX2 / Basis
// transcoder (public/basis). Every model in the scene loads the same way,
// whether its textures are WebP or KTX2 (KHR_texture_basisu), with no CDN
// dependency. Returns exactly what useGLTF returns ({ nodes, materials,
// scene, … }), so it's a straight swap.
//
// Note: no .preload() — preloading would need to parse KTX2 textures
// without a renderer to detectSupport against. Models load on mount instead.

import { use } from 'react'
import { useGLTF } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js'
import {
  NearestFilter, LinearFilter, NearestMipmapNearestFilter, LinearMipmapNearestFilter,
  NearestMipmapLinearFilter, LinearMipmapLinearFilter,
  ClampToEdgeWrapping, MirroredRepeatWrapping, RepeatWrapping,
} from 'three'

// (exported for the vending machine's drop, which draws in a little WebGL canvas of its own and loads its model with
// these same decoders and the buffered KTX2 path: components/ui/VendingDrop.jsx)
export const DRACO_PATH = '/draco/'
export const BASIS_PATH = '/basis/'

// One KTX2Loader for the whole app; detectSupport() runs once against the
// (WebGPU) renderer — three's KTX2Loader supports it directly.
let _ktx2 = null
/**
 * The app's one KTX2 loader. Exported because loose .ktx2 files (billboard
 * placeholder art) need it too, and a second instance would mean a second
 * transcoder worker pool for the sake of one texture.
 */
export function getKTX2(gl) {
  if (!_ktx2) {
    _ktx2 = new KTX2Loader().setTranscoderPath(BASIS_PATH)
    _ktx2.detectSupport(gl)
  }
  return _ktx2
}

// KTX2 inside a GLB, handed to the transcoder from the model's own buffer.
//
// The stock loader (three-stdlib's GLTFLoader, through drei) copies each
// embedded image into a Blob, makes an object URL of it, and the KTX2
// loader then fetches that URL back before its workers transcode it. Making
// a Blob is a synchronous round trip to the browser process: free when the
// browser is idle, and 5.2 s of a frozen main thread on the live site's
// first cold load after the 28 Oct deploy, when it was not
// (docs/world-performance.md). Draco and the transcoding were already in
// workers; this was the one piece of decoding left on the main thread.
// Here the buffer goes straight to the workers,
// and the texture comes out exactly as the stock path makes it: same
// cache key, flipY false, the sampler's filters and wrapping, the name.
// A KTX2 the GLB points at by URL goes the stock way.
const GL_FILTERS = {
  9728: NearestFilter, 9729: LinearFilter, 9984: NearestMipmapNearestFilter,
  9985: LinearMipmapNearestFilter, 9986: NearestMipmapLinearFilter, 9987: LinearMipmapLinearFilter,
}
const GL_WRAPPING = { 33071: ClampToEdgeWrapping, 33648: MirroredRepeatWrapping, 10497: RepeatWrapping }

class BufferedKTX2 {
  constructor(parser) {
    this.parser = parser
    // the name the stock extension registers under, so this replaces it
    this.name = 'KHR_texture_basisu'
  }

  loadTexture(textureIndex) {
    const parser = this.parser
    const json = parser.json
    const textureDef = json.textures[textureIndex]
    const ext = textureDef.extensions?.KHR_texture_basisu
    if (!ext) return null
    const ktx2 = parser.options.ktx2Loader
    if (!ktx2) throw new Error('THREE.GLTFLoader: setKTX2Loader must be called before loading KTX2 textures')
    const sourceDef = json.images[ext.source]
    if (sourceDef.bufferView === undefined) return parser.loadTextureImage(textureIndex, ext.source, ktx2)

    const cacheKey = sourceDef.bufferView + ':' + textureDef.sampler
    if (parser.textureCache[cacheKey]) return parser.textureCache[cacheKey]
    const promise = parser.getDependency('bufferView', sourceDef.bufferView)
      // a copy: the transcoder transfers what it is given to its worker,
      // and the parser keeps this bufferView cached for anything else that
      // points at the same image (a memcpy, not a trip to the browser)
      .then((buffer) => new Promise((resolve, reject) => ktx2.parse(buffer.slice(0), resolve, reject)))
      .then((texture) => {
        texture.flipY = false
        texture.name = textureDef.name || sourceDef.name || ''
        const sampler = (json.samplers || {})[textureDef.sampler] || {}
        texture.magFilter = GL_FILTERS[sampler.magFilter] || LinearFilter
        texture.minFilter = GL_FILTERS[sampler.minFilter] || LinearMipmapLinearFilter
        texture.wrapS = GL_WRAPPING[sampler.wrapS] || RepeatWrapping
        texture.wrapT = GL_WRAPPING[sampler.wrapT] || RepeatWrapping
        texture.userData.mimeType = sourceDef.mimeType || 'image/ktx2'
        parser.associations.set(texture, { textures: textureIndex })
        return texture
      })
      .catch(() => null)
    parser.textureCache[cacheKey] = promise
    return promise
  }
}
// one function, so register() (which dedupes by identity) keeps one copy
export const bufferedKTX2 = (parser) => new BufferedKTX2(parser)

// Loose .ktx2 files, one promise each for the life of the page.
const _loose = new Map()
function ktx2Texture(gl, url) {
  if (!_loose.has(url)) _loose.set(url, getKTX2(gl).loadAsync(url))
  return _loose.get(url)
}

/**
 * A loose .ktx2 as a texture, suspending like drei's useTexture does, so it
 * is there behind the veil with the models (the loader is on the default
 * loading manager, which the veil counts) and the material it goes into is
 * built once, with it. Comes back with flipY false, as every compressed
 * texture does: encode with --flip where an image was loaded flipped
 * (scripts/ktx2-textures.mjs).
 */
export function useKTX2Texture(url) {
  const gl = useThree((s) => s.gl)
  return use(ktx2Texture(gl, url))
}

/** Start loading without suspending, so they come in alongside a model that suspends first. */
export function preloadKTX2(gl, urls) {
  for (const u of urls) ktx2Texture(gl, u)
}

/** Several at once, in parallel; same order as `urls`. */
export function useKTX2Textures(urls) {
  const gl = useThree((s) => s.gl)
  const all = urls.map((u) => ktx2Texture(gl, u))
  return all.map((p) => use(p))
}

export function useModel(path) {
  const gl = useThree((s) => s.gl)
  return useGLTF(path, DRACO_PATH, undefined, (loader) => {
    if (typeof loader.setKTX2Loader === 'function') {
      loader.setKTX2Loader(getKTX2(gl))
      loader.register(bufferedKTX2)
    }
  })
}

export default useModel
